import { Form, Modal, type FormProps } from '@yimoka/antd';
import { useForm, useStore } from '@yimoka/react';
import type { IStore } from '@yimoka/store';
import { useCallback, useState } from 'react';

import { useTranslation } from '@/locales/i18n';

export type AgreementLoginFormProps = FormProps & { store?: IStore };

/** 登录表单协议门禁：未勾选时先取得明确同意，再回写状态并复用正常提交链路。 */
export function AgreementLoginForm(props: AgreementLoginFormProps) {
  const { store, onSubmitCapture, ...formProps } = props;
  const { t } = useTranslation();
  const form = useForm();
  const curStore = useStore(store);
  const [agreementPromptOpen, setAgreementPromptOpen] = useState(false);

  const submit = useCallback(() => {
    // Formily 自行展示字段校验错误；Store 统一处理接口错误通知。
    void form.submit()
      .then(() => curStore?.fetch())
      .catch(() => undefined);
  }, [curStore, form]);

  const handleSubmitCapture: NonNullable<FormProps['onSubmitCapture']> = useCallback((event) => {
    onSubmitCapture?.(event);
    if (event.defaultPrevented) return;
    event.preventDefault();

    if (form.values.agreement === true) {
      submit();
      return;
    }
    setAgreementPromptOpen(true);
  }, [form, onSubmitCapture, submit]);

  const handleAgreementConfirm = useCallback(() => {
    // 用户明确确认后同步回写表单，使复选框状态与本次登录请求保持一致。
    form.setValuesIn('agreement', true);
    setAgreementPromptOpen(false);
    submit();
  }, [form, submit]);

  return (
    <>
      <Form {...formProps} onSubmitCapture={handleSubmitCapture} />
      <Modal
        closeOnOk
        destroyOnHidden
        cancelText={t('Account:login.agreementConfirmCancel')}
        okText={t('Account:login.agreementConfirmOk')}
        open={agreementPromptOpen}
        title={t('Account:login.agreementConfirmTitle')}
        trigger={false}
        onCancel={() => setAgreementPromptOpen(false)}
        onOk={handleAgreementConfirm}
      >
        {t('Account:login.agreementConfirmContent')}
      </Modal>
    </>
  );
}
